import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { api } from "../lib/api";
import { PageHeader } from "../components/layout/AppLayout";
import { MapPin, Users, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

// Fix leaflet default icon paths (Webpack breaks them)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const TYPE_COLOR = {
  engineer: "#FFC107",
  field_tech: "#4EA1FF",
  both: "#22C55E",
};

export default function TalentMap() {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterVendor, setFilterVendor] = useState("");
  const [filterType, setFilterType] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/talent-map/personnel");
      setPeople(r.data.personnel || []);
      if (r.data.geocoded_now > 0) toast.success(`Located ${r.data.geocoded_now} new team members on the map`);
    } catch { toast.error("Could not load map"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const filtered = people.filter((p) =>
    (!filterVendor || (p.vendors || []).includes(filterVendor)) &&
    (!filterType || (p.person_type || []).includes(filterType))
  );
  const pinned = filtered.filter((p) => p.latitude != null && p.longitude != null);
  const unpinned = filtered.filter((p) => p.latitude == null || p.longitude == null);

  const regionCounts = useMemo(() => {
    const map = {};
    filtered.forEach((p) => {
      const k = p.region || "— No region set —";
      map[k] = (map[k] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [filtered]);

  const initialCenter = pinned.length > 0
    ? [pinned[0].latitude, pinned[0].longitude]
    : [-25.2744, 133.7751]; // Australia default

  const vendors = ["Cisco", "Fortinet", "Palo Alto", "F5", "Checkpoint"];

  return (
    <div>
      <PageHeader
        overline="Coverage"
        title="Talent Availability Map"
        subtitle="See where your engineers and field technicians are based across regions. Instantly spot coverage gaps by city or vendor specialisation."
        actions={
          <Button data-testid="map-refresh" onClick={refresh} disabled={refreshing} variant="outline" className="border-slate-300 bg-white hover:bg-slate-50 text-[#0F1E38] font-mono uppercase tracking-wider text-xs">
            <ArrowsClockwise size={14} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mr-2">Filter:</span>
        <select data-testid="map-filter-vendor" value={filterVendor} onChange={(e) => setFilterVendor(e.target.value)}
          className="h-8 px-2 rounded border border-slate-300 bg-white text-xs font-mono">
          <option value="">All vendors</option>
          {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="map-filter-type" value={filterType} onChange={(e) => setFilterType(e.target.value)}
          className="h-8 px-2 rounded border border-slate-300 bg-white text-xs font-mono">
          <option value="">All types</option>
          <option value="engineer">Engineers</option>
          <option value="field_tech">Field Techs</option>
        </select>
        <div className="ml-auto flex items-center gap-3 text-[11px] font-mono uppercase text-slate-500">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: TYPE_COLOR.engineer }}></span>Engineer</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: TYPE_COLOR.field_tech }}></span>Field Tech</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: TYPE_COLOR.both }}></span>Both</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Map */}
        <div className="lg:col-span-3">
          <div data-testid="talent-map" className="border border-slate-200 rounded-lg overflow-hidden bg-white" style={{ height: 600 }}>
            {loading ? (
              <div className="h-full flex items-center justify-center text-xs font-mono uppercase text-slate-500">Loading map…</div>
            ) : (
              <MapContainer center={initialCenter} zoom={4} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {pinned.map((p) => {
                  const isBoth = (p.person_type || []).length > 1;
                  const color = isBoth ? TYPE_COLOR.both : (TYPE_COLOR[(p.person_type || [])[0]] || TYPE_COLOR.engineer);
                  return (
                    <CircleMarker
                      key={p.id}
                      center={[p.latitude, p.longitude]}
                      radius={10}
                      pathOptions={{ color: "#0F1E38", weight: 2, fillColor: color, fillOpacity: 0.85 }}
                    >
                      <Popup>
                        <div style={{ minWidth: 200 }}>
                          <div className="font-semibold text-[#0F1E38]">{p.name}</div>
                          <div className="text-xs text-slate-500 mb-2">{p.role_title || "—"}</div>
                          <div className="text-xs"><strong>Region:</strong> {p.region || "—"}</div>
                          <div className="text-xs"><strong>Rate:</strong> {p.hourly_rate ? `$${p.hourly_rate}/hr` : "—"}</div>
                          <div className="text-xs"><strong>Vendors:</strong> {(p.vendors || []).join(", ") || "—"}</div>
                          <div className="text-xs"><strong>Status:</strong> {p.status}</div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>
            )}
          </div>
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          <div className="border border-slate-200 rounded-lg p-4 bg-white">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-3">Coverage by region</div>
            {regionCounts.length === 0 ? (
              <div className="text-xs text-slate-500">No personnel matching filters</div>
            ) : (
              <div className="space-y-1.5" data-testid="region-list">
                {regionCounts.map(([r, count]) => (
                  <div key={r} className="flex items-center justify-between text-sm">
                    <span className="text-[#0F1E38] truncate" title={r}>{r}</span>
                    <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border border-slate-200 rounded-lg p-4 bg-white">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">On the map</div>
              <div className="flex items-center gap-1 text-emerald-600"><MapPin size={13} weight="fill" /> <span className="font-mono text-sm">{pinned.length}</span></div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">No location yet</div>
              <div className="flex items-center gap-1 text-slate-500"><Users size={13} /> <span className="font-mono text-sm">{unpinned.length}</span></div>
            </div>
            {unpinned.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200 text-[11px] text-slate-500 leading-relaxed">
                {unpinned.length} team member{unpinned.length === 1 ? "" : "s"} have no region on file yet. Add a city or address to their Personnel record and refresh.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
